import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import TabGroup from "@/components/ui/tab";
import Dropdown from "@/components/ui/dropdown";
import { ArrowLeft, ArrowRight } from "@/components/icons";
import { renderPaginationNumbers } from "@/utils/pagination";
import type { FilterField, SortOption } from "@/types/component";
import type {
  ClientAnnouncement,
  ClientAnnouncementCategory,
} from "../types/client-announcements.types";
import { mapAnnouncementListItemToClient } from "../types/client-announcements.types";
import { clientAnnouncementsApi } from "../services/client-announcements.api";
import AnnouncementCard from "../components/AnnouncementCard";
import AnnouncementViewModal from "../components/AnnouncementViewModal";
import styles from "./ClientAnnouncementsPage.module.scss";

const PAGE_SIZE_OPTIONS = [12, 24, 48];

/** Convert daterange picker value [Date, Date] to ISO strings for API */
function parseDateRangeValue(value: unknown): {
  dateFrom?: string;
  dateTo?: string;
} {
  if (!value || !Array.isArray(value)) return {};
  const [start, end] = value as [Date | null, Date | null];
  return {
    dateFrom: start ? start.toISOString().slice(0, 10) : undefined,
    dateTo: end ? end.toISOString().slice(0, 10) : undefined,
  };
}

const ClientAnnouncementsPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState(() => {
    const stateTab = (location.state as { activeTab?: number } | null)
      ?.activeTab;
    return typeof stateTab === "number" ? stateTab : 0;
  });
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [sortValue, setSortValue] = useState("startDate_desc");
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<ClientAnnouncement | null>(null);
  const [categories, setCategories] = useState<ClientAnnouncementCategory[]>(
    [],
  );
  const [items, setItems] = useState<ClientAnnouncement[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    clientAnnouncementsApi
      .getCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  const tabs = [
    { label: t("clientAnnouncements.tabs.general"), content: null },
    { label: t("clientAnnouncements.tabs.special"), content: null },
  ];

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        name: "category",
        label: t("clientAnnouncements.filters.category"),
        type: "dropdown",
        placeholder: t("clientAnnouncements.filters.categoryPlaceholder"),
        options: [
          {
            label: t("clientAnnouncements.filters.allCategories"),
            value: "",
          },
          ...categories.map((c) => ({ label: c.name, value: c.id })),
        ],
      },
      {
        name: "dateRange",
        label: t("clientAnnouncements.filters.dateRange"),
        type: "daterange",
        placeholder: t("clientAnnouncements.filters.dateRangePlaceholder"),
      },
    ],
    [t, categories],
  );

  const sortOptions: SortOption[] = [
    {
      label: t("clientAnnouncements.sort.newest"),
      value: "startDate_desc",
    },
    {
      label: t("clientAnnouncements.sort.oldest"),
      value: "startDate_asc",
    },
    {
      label: t("clientAnnouncements.sort.titleAZ"),
      value: "title_asc",
    },
    {
      label: t("clientAnnouncements.sort.titleZA"),
      value: "title_desc",
    },
  ];

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const stateTab = (location.state as { activeTab?: number } | null)
      ?.activeTab;
    if (typeof stateTab === "number" && stateTab !== activeTab) {
      setActiveTab(stateTab);
      setCurrentPage(1);
    }
  }, [activeTab, location.state]);

  const typeParam = activeTab === 0 ? "general" : "private";
  const dateRange = parseDateRangeValue(filterValues.dateRange);

  // Parse sort value: "field_order" -> { sortBy, sortOrder }
  const [sortBy, sortOrder] = useMemo(() => {
    const [field, order] = sortValue.split("_");
    return [
      field as "startDate" | "createdAt" | "title",
      order as "asc" | "desc",
    ];
  }, [sortValue]);

  useEffect(() => {
    setLoading(true);
    clientAnnouncementsApi
      .getAnnouncements({
        type: typeParam,
        search: debouncedSearch.trim() || undefined,
        dateFrom: dateRange.dateFrom,
        dateTo: dateRange.dateTo,
        sortBy,
        sortOrder,
        page: currentPage,
        limit: pageSize,
      })
      .then((res) => {
        const mapped = (res.items || []).map(mapAnnouncementListItemToClient);
        setItems(mapped);
        setTotalRecords(res.total ?? 0);
      })
      .catch(() => {
        setItems([]);
        setTotalRecords(0);
      })
      .finally(() => setLoading(false));
  }, [
    typeParam,
    debouncedSearch,
    dateRange.dateFrom,
    dateRange.dateTo,
    sortBy,
    sortOrder,
    currentPage,
    pageSize,
  ]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const paginatedItems = items;

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setCurrentPage(1);
  };
  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };
  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };
  const handleCardClick = (announcement: ClientAnnouncement) => {
    setSelectedAnnouncement(announcement);
    clientAnnouncementsApi
      .getAnnouncementById(announcement.id)
      .then((detail) => {
        const mapped = mapAnnouncementListItemToClient(detail);
        setSelectedAnnouncement(mapped);
      })
      .catch(() => {});
  };

  return (
    <DashboardLayout>
      <Header title={t("clientAnnouncements.title")} />

      <AnnouncementViewModal
        announcement={selectedAnnouncement}
        isOpen={Boolean(selectedAnnouncement)}
        onClose={() => setSelectedAnnouncement(null)}
      />

      <div className={styles.pageContainer}>
        <TabGroup
          tabs={tabs}
          defaultActiveTab={activeTab}
          onTabChange={handleTabChange}
          orientation="horizontal"
        />

        <PageHeader
          searchPlaceholder={t("clientAnnouncements.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={setSearchTerm}
          filters={filterFields}
          onFilterChange={handleFilterChange}
          showSort
          sortOptions={sortOptions}
          onSort={(option) => setSortValue(option?.value ?? "startDate_desc")}
        />

        {loading ? (
          <div className={styles.emptyState}>
            {t("clientAnnouncements.loading")}
          </div>
        ) : paginatedItems.length === 0 ? (
          <div className={styles.emptyState}>
            {t("clientAnnouncements.noData")}
          </div>
        ) : (
          <>
            <div className={styles.announcementGrid}>
              {paginatedItems.map((announcement) => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  onClick={() => handleCardClick(announcement)}
                />
              ))}
            </div>

            <div className={styles.paginationBar}>
              <div className={styles.paginationLeft}>
                <span className={styles.paginationLabel}>
                  {t("pagination.show")}
                </span>
                <Dropdown
                  onSelect={(v) =>
                    handlePageSizeChange(Array.isArray(v) ? v[0] : v)
                  }
                  options={PAGE_SIZE_OPTIONS.map((size) => ({
                    label: size.toString(),
                    value: size.toString(),
                  }))}
                  menuPosition="top"
                  selectedLabel={pageSize.toString()}
                  value={pageSize.toString()}
                />
                <span className={styles.paginationDetails}>
                  {t("pagination.details", {
                    total: totalRecords,
                    start:
                      totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1,
                    end: Math.min(currentPage * pageSize, totalRecords),
                  })}
                </span>
              </div>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <ArrowLeft height={16} width={16} />
                </button>
                {renderPaginationNumbers(
                  currentPage,
                  totalPages,
                  handlePageChange,
                )}
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <ArrowRight height={16} width={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ClientAnnouncementsPage;
